import { useState } from 'react';
import { X, User, Mail, Phone, CheckCircle, Loader, MessageSquare } from 'lucide-react';
import PropTypes from 'prop-types';
import { googleSheetsService } from '../utils/google-sheets';

const InputField = ({ icon, error, ...props }) => (
  <div className="relative">
    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
      {icon}
    </div>
    <input
      className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 text-sm sm:text-base ${
        error ? 'border-red-500' : 'border-gray-200'
      } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
      {...props}
    />
    {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
  </div>
);

const QuotationModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service: '',
    location: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });

  // Indian mobile number validation
  const isValidIndianMobile = (phone) => {
    // Remove any non-digit characters for validation
    const cleaned = phone.replace(/\D/g, '');
    // Indian mobile numbers: 10 digits starting with 6, 7, 8, or 9
    const indianMobileRegex = /^[6-9]\d{9}$/;
    return indianMobileRegex.test(cleaned);
  };

  // Name validation (letters, spaces, and common name characters)
  const isValidName = (name) => {
    // Allow letters, spaces, hyphens, apostrophes, and dots
    const nameRegex = /^[a-zA-Z\s\-'.]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!isValidName(formData.name)) {
      newErrors.name = 'Please enter a valid name (letters, spaces, hyphens, apostrophes only)';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidIndianMobile(formData.phone)) {
      newErrors.phone = 'Please enter a valid Indian mobile number (10 digits starting with 6, 7, 8, or 9)';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please describe your requirements';
    }

    if (!formData.service.trim()) {
      newErrors.service = 'Please select a service type';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Please enter your location';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });
    setErrors({});

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    // Quotation limit check commented out for testing
    /*
    if (!SessionManager.canSubmitQuote(formData.phone)) {
      setSubmitStatus({
        success: false,
        message: 'Quote submission limit reached or session expired'
      });
      setIsSubmitting(false);
      return;
    }
    */

    try {
      // Submit quotation to Google Sheets
      try {
        await googleSheetsService.submitQuotation({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          service: formData.service,
          location: formData.location
        });
        
        setSubmitStatus({
          success: true,
          message: 'Quote submitted successfully! We will contact you soon.'
        });
      } catch (sheetsError) {
        console.error('Google Sheets submission failed:', sheetsError);
        throw new Error('Failed to submit quote request. Please try again later.');
      }

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setSubmitStatus({
        success: false,
        message: error.message || 'Failed to submit quote. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center pt-20 pb-1 px-1 sm:pt-24 sm:pb-2 sm:px-2 md:pt-24 md:pb-4 md:px-4 ${
        isOpen ? 'visible' : 'invisible'
      }`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[95vw] sm:max-w-sm md:max-w-md bg-white rounded-xl shadow-lg overflow-hidden max-h-[75vh] sm:max-h-[80vh] md:max-h-[85vh] lg:max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-4 sm:p-6 lg:p-8">
          {submitStatus.success ? (
            <div className="text-center">
              <CheckCircle className="w-16 h-16 mx-auto text-blue-500" />
              <h3 className="mt-4 text-xl font-semibold">Thank You!</h3>
              <p className="mt-2 text-gray-600">
                We&apos;ve received your request and will contact you soon.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-center mb-2">Get a Quote</h2>
              <p className="text-center text-gray-600 mb-8">
                Tell us about your project requirements
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <InputField
                  icon={<User className="w-5 h-5 text-gray-400" />}
                  type="text"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  error={errors.name}
                />
                <InputField
                  icon={<Mail className="w-5 h-5 text-gray-400" />}
                  type="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  error={errors.email}
                />
                <InputField
                  icon={<Phone className="w-5 h-5 text-gray-400" />}
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  error={errors.phone}
                />
                
                {/* Service Type Dropdown */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <select
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border-2 text-sm sm:text-base ${
                      errors.service ? 'border-red-500' : 'border-gray-200'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white`}
                  >
                    <option value="">Select Service Type</option>
                    <option value="Residential Construction">Residential Construction</option>
                    <option value="Commercial Construction">Commercial Construction</option>
                    <option value="Renovation & Remodeling">Renovation & Remodeling</option>
                    <option value="Interior Design">Interior Design</option>
                    <option value="Architectural Services">Architectural Services</option>
                    <option value="Structural Engineering">Structural Engineering</option>
                    <option value="Project Management">Project Management</option>
                    <option value="Custom Quote">Custom Quote</option>
                  </select>
                  {errors.service && <p className="mt-1 text-sm text-red-500">{errors.service}</p>}
                </div>

                <InputField
                  icon={<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>}
                  type="text"
                  placeholder="Your Location (City, State)"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  error={errors.location}
                />
                
                <div className="relative">
                  <div className="absolute top-3 left-4">
                    <MessageSquare className="w-5 h-5 text-gray-400" />
                  </div>
                  <textarea
                    placeholder="Describe your requirements"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={4}
                    className={`w-full pl-12 pr-4 py-3 border-2 ${
                      errors.message ? 'border-red-500' : 'border-gray-200'
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">{errors.message}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg font-medium text-sm sm:text-base
                           hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <Loader className="w-5 h-5 mx-auto animate-spin" />
                  ) : (
                    'Submit Quote Request'
                  )}
                </button>
                {submitStatus.message && (
                  <p className={`text-sm text-center ${
                    submitStatus.success ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {submitStatus.message}
                  </p>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

QuotationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

InputField.propTypes = {
  icon: PropTypes.node.isRequired,
  error: PropTypes.string
};

export default QuotationModal;
